/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import 'react-toastify/dist/ReactToastify.css'
import notifyToast from '../../components/Toast/Toast'
import {ToastContainer} from 'react-toastify'
import TitleComponent from '../../components/TitleComponent/TitleComponent'
import {useParams} from 'react-router-dom'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import {RepromaterijalPromet} from '../../models/RepromaterijaliPromet'
import PoslovniceDropdown from '../../components/PoslovniceDropdown/PoslovniceDropdown'
import LokacijeRepromaterijalaDropdown from '../../components/LokacijeRepromaterijalaDropdown/LokacijeRepromaterijalaDropdown'
import QrCodeScanner from '../../components/QrCodeScanner/QrCodeScanner'
import {
    fetchRepromaterijalPromet,
    saveRepromaterijalPromet,
    updateRepromaterijalPromet,
} from '../../api/prometRepromaterijala'
import useDebounce from '../../hooks/useDebounce'
import OdjeliDropdown from '../../components/OdjeliDropdown/OdjeliDropdown'
import {rolnaDetaljiProizvodnja} from '../../api/fmkNalozi'
import {RolnaDetaljiProizvodnja} from '../../models/FmkNaloziModel'

interface PrometDetailsProps {}

/** Zebra + general cleanup (same concept as KrajRolne) */
const normalizeScan = (value: string) =>
    String(value ?? '')
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const extractBrojRolneFromQr = (raw: string): string => {
    const first = String(raw || '').split(';')[0] ?? ''
    return first.trim()
}

const isIdOnly = (raw: string) => /^\d+$/.test(normalizeScan(raw))

const isCompanyAlternativaDoo = (s: string) => {
    const cleaned = normalizeScan(s)
        .toLowerCase()
        .replace(/[^a-z]/g, '')
    return cleaned === 'alternativadoo'
}

const isFullRolnaQr = (raw: string) => {
    const s = normalizeScan(raw)
    if (!s) return false

    const parts = s
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)

    if (parts.length < 5) return false
    const last = parts[parts.length - 1] ?? ''
    return isCompanyAlternativaDoo(last)
}

const PrometDetails: React.FC<PrometDetailsProps> = () => {
    const {id} = useParams()
    const idFromURL = Number(id)

    const initialValues = {
        Datum: '',
        Repromaterijal: '',
        VrstaIzlaza: '',
        BrojDokumentaIzlaza: '',
        Krajnje: '', // keep as '' for empty state
        LokacijaRolne: '',
        Odjel: '',
        Poslovnica: '',
        Napomene: '',
        Naziv: '',
        Promet: '',
        Skenirano: 0,
    }

    const [loading, setLoading] = useState(true)
    const [initialData, setInitialData] = useState<Partial<RepromaterijalPromet>>(initialValues)
    const [rolnaDetaljiInfo, setRolnaDetaljiInfo] = useState<RolnaDetaljiProizvodnja | any>()

    // "KrajRolne" concept for a single rolna field
    const [rawScan, setRawScan] = useState<string>('')
    const [scannedByDevice, setScannedByDevice] = useState<number>(0)
    const [skenirano, setSkenirano] = useState<number>(0)

    useEffect(() => {
        if (idFromURL) {
            fetchRepromaterijalPromet(idFromURL)
                .then((data) => {
                    setInitialData(data)
                    formik.resetForm({values: data})
                    setRawScan('')
                    setScannedByDevice(0)
                    setSkenirano(0)
                })
                .finally(() => setLoading(false))
        } else {
            setLoading(false)
        }
    }, [idFromURL])

    const validationSchema = Yup.object({
        Krajnje: Yup.number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) return undefined
                return Number.isNaN(value) ? undefined : value
            })
            .typeError('Unesite broj')
            .required('Required'),

        BrojDokumentaIzlaza: Yup.string().min(5, 'Must be at least 4 characters').required('Required'),
        Datum: Yup.string().required('Required'),
        Repromaterijal: Yup.number().required('Required'),
        Odjel: Yup.number().required('Required'),
        VrstaIzlaza: Yup.number().required('Required'),
        Poslovnica: Yup.number().required('Required'),
        LokacijaRolne: Yup.string().required('Required'),
    })

    const formik = useFormik({
        initialValues: initialData,
        validationSchema,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true)
            try {
                const response = idFromURL
                    ? await updateRepromaterijalPromet(values, idFromURL)
                    : await saveRepromaterijalPromet(values)

                if (response?.error === 0) notifyToast(true, response.message)
                else notifyToast(false, response?.message || 'An unexpected error occurred.')
            } catch (error: any) {
                console.error('Error: ', error)
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška')
                setStatus(error)
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })

    // fetch rolna details when Repromaterijal changes
    const debouncedRolna = useDebounce(formik.values.Repromaterijal)
    useEffect(() => {
        if (debouncedRolna) {
            rolnaDetaljiProizvodnja({rolnaId: debouncedRolna})
                .then((response) => setRolnaDetaljiInfo(response?.data ?? response))
                .catch((error) =>
                    notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška pri učitavanju rolne')
                )
        } else {
            setRolnaDetaljiInfo(undefined)
        }
    }, [debouncedRolna])

    const isScanned = useMemo(() => Number(skenirano) === 1, [skenirano])

    const applyDeviceScanRolna = (data: any) => {
        const cleaned = normalizeScan(String(data || ''))
        if (!cleaned) return

        const broj = extractBrojRolneFromQr(cleaned)

        setRawScan(cleaned)
        setScannedByDevice(1)

        const sk = isIdOnly(cleaned) || isFullRolnaQr(cleaned) ? 1 : 0
        formik.setFieldValue('Skenirano', sk)
        setSkenirano(sk)

        formik.setFieldValue('Repromaterijal', broj)
    }

    const handleManualRolnaChange = (value: string) => {
        formik.setFieldValue('Repromaterijal', value)
        setRawScan('')
        setScannedByDevice(0)
        setSkenirano(0)
    }

    const disableSubmit = loading || formik.isSubmitting || !formik.isValid || !!rolnaDetaljiInfo?.message

    /** small helper for consistent field error rendering */
    const FieldError = ({name}: {name: keyof typeof initialValues}) => {
        const touched = (formik.touched as any)[name]
        const err = (formik.errors as any)[name]
        if (!touched || !err) return null
        return <div className='text-danger small mt-1'>{String(err)}</div>
    }
    const extractNalogFromScan = (raw: string) => {
        const s = normalizeScan(raw)
        if (!s) return ''
        // take first meaningful segment if it's QR with ';'
        const first = s.split(';').map(x => x.trim()).filter(Boolean)[0] ?? ''
        return first
    }
    return (
        <div className='card mb-5 mb-xl-10'>
            <ToastContainer />

            {/* Header */}
            <div className='card-header border-0'>
                <div className='card-title m-0 d-flex align-items-center flex-wrap gap-2'>
                    <TitleComponent name='promet' />

                    <span
                        className={clsx('badge', isScanned ? 'badge-light-success' : 'badge-light-danger')}
                        title={
                            isScanned
                                ? 'Skenirano (device scan)'
                                : 'Nije skenirano (ručni unos se ne računa kao skeniranje)'
                        }
                    >
            {isScanned ? (
                <>
                    <i className='bi bi-check-circle-fill me-1' /> Skenirano
                </>
            ) : (
                <>
                    <i className='bi bi-x-circle-fill me-1' /> Nije skenirano
                </>
            )}
          </span>
                </div>
            </div>

            {/* Body */}
            <div className='card-body p-0'>
                {loading ? (
                    <div className='p-9'>
                        <LoadingComponent />
                    </div>
                ) : (
                    <form onSubmit={formik.handleSubmit} noValidate className='form'>
                        <div className='p-3 p-md-6 p-xl-8'>
                            {/* Top instruction / status */}
                            <div className='alert alert-light border d-flex align-items-start gap-3 mb-4 mb-md-6'>
                                <i className='bi bi-info-circle fs-4 mt-1'></i>
                                <div className='flex-grow-1'>
                                    <div className='fw-semibold text-dark'>Brzi unos</div>
                                    <div className='text-muted'>
                                        1) Skeniraj rolnu → 2) Unesi krajnju količinu → 3) Učitaj detalje → 4) Popuni ostala polja → 5) Potvrdi.
                                        Ručni unos rolne se ne smatra skeniranjem.
                                    </div>
                                </div>
                            </div>

                            <div className='row g-4 g-lg-6'>
                                {/* LEFT: FORM */}
                                <div className='col-12 col-lg-7'>
                                    {/* Section: Rolna + Krajnje (moved here) */}
                                    <div className='card shadow-sm border-0'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='d-flex align-items-start justify-content-between gap-3 flex-wrap'>
                                                <div>
                                                    <div className='fw-bold text-dark'>1) Rolna</div>
                                                    <div className='text-muted small'>Skeniraj ili upiši broj rolne, zatim unesi krajnju količinu</div>
                                                </div>

                                                <span className={clsx('badge', isScanned ? 'bg-success' : 'bg-danger')}>
                          {isScanned ? (
                              <>
                                  <i className='bi bi-check-circle-fill me-1' /> Skenirano
                              </>
                          ) : (
                              <>
                                  <i className='bi bi-x-circle-fill me-1' /> Nije skenirano
                              </>
                          )}
                        </span>
                                            </div>

                                            <div className='row g-3 align-items-end mt-1'>
                                                {/* Scanner */}
                                                <div className='col-12 col-md-4 col-xl-3'>
                                                    <label className='form-label fw-semibold'>Skeniraj</label>
                                                    <div className='d-flex align-items-center gap-2'>
                                                        <QrCodeScanner onScan={applyDeviceScanRolna} />
                                                        <span className='text-muted small'>QR</span>
                                                    </div>
                                                </div>

                                                {/* Rolna */}
                                                <div className='col-12 col-md-8 col-xl-9'>
                                                    <label className='form-label fw-semibold required'>Broj rolne</label>
                                                    <input
                                                        type='text'
                                                        placeholder='Skeniraj ili upiši broj rolne'
                                                        {...formik.getFieldProps('Repromaterijal')}
                                                        onChange={(e) => handleManualRolnaChange(e.target.value)}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': formik.touched.Repromaterijal && formik.errors.Repromaterijal,
                                                            'is-valid': formik.touched.Repromaterijal && !formik.errors.Repromaterijal,
                                                        })}
                                                    />
                                                    <FieldError name='Repromaterijal' />

                                                    {scannedByDevice === 1 && rawScan && (
                                                        <div className='text-muted small mt-2 d-none d-md-block'>
                                                            <i className='bi bi-upc-scan me-1' />
                                                            Broj rolne: <span className='fw-semibold'>{rawScan}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* ✅ Krajnja količina (moved under broj rolne) */}
                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold required'>Krajnja količina</label>
                                                    <input
                                                        type='number'
                                                        inputMode='decimal'
                                                        placeholder='0'
                                                        // IMPORTANT: never let NaN go into value prop
                                                        value={formik.values.Krajnje === '' || formik.values.Krajnje === null ? '' : String(formik.values.Krajnje)}
                                                        onChange={(e) => {
                                                            const raw = e.target.value
                                                            // keep '' when empty, store number when filled
                                                            formik.setFieldValue('Krajnje', raw === '' ? '' : Number(raw))
                                                        }}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': formik.touched.Krajnje && formik.errors.Krajnje,
                                                            'is-valid': formik.touched.Krajnje && !formik.errors.Krajnje,
                                                        })}
                                                    />
                                                    <FieldError name='Krajnje' />
                                                </div>

                                                {/* Optional: show max quantity hint if you have details */}
                                                <div className='col-12 col-md-6'>
                                                    <div className='text-muted small mt-2 mt-md-7'>
                                                        {rolnaDetaljiInfo && !rolnaDetaljiInfo?.message ? (
                                                            <>
                                                                Maks. dostupno:{' '}
                                                                <b>{rolnaDetaljiInfo?.ReprMatTrenutnaKolicina ?? 'N/A'}</b>
                                                            </>
                                                        ) : (
                                                            <span>&nbsp;</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Inline details on mobile/tablet */}
                                            <div className='mt-3 d-lg-none'>
                                                {!rolnaDetaljiInfo ? (
                                                    <div className='alert alert-light border mb-0'>
                                                        <span className='text-muted'>Unesite rolna da se učitaju detalji.</span>
                                                    </div>
                                                ) : rolnaDetaljiInfo?.message ? (
                                                    <div className='alert alert-warning mb-0'>
                                                        <b>Poruka:</b> {rolnaDetaljiInfo.message}
                                                    </div>
                                                ) : (
                                                    <div className='alert alert-primary mb-0'>
                                                        <div className='d-flex flex-column flex-md-row flex-wrap gap-2'>
                                                            <div>
                                                                Težina: <b>{rolnaDetaljiInfo.ReprMatTrenutnaKolicina || 'N/A'}</b>
                                                            </div>
                                                            <div className='d-none d-md-block'>|</div>
                                                            <div>
                                                                Debljina: <b>{rolnaDetaljiInfo.ReprMatDebljina || 'N/A'}</b>
                                                            </div>
                                                            <div className='d-none d-md-block'>|</div>
                                                            <div>
                                                                Širina: <b>{rolnaDetaljiInfo.ReprMatSirina || 'N/A'}</b>
                                                            </div>
                                                            <div className='d-none d-md-block'>|</div>
                                                            <div>
                                                                Boja: <b>{rolnaDetaljiInfo.boja_lica?.BojaNaziv || 'N/A'}</b>
                                                            </div>
                                                            <div className='d-none d-md-block'>|</div>
                                                            <div>
                                                                Proizvođač: <b>{rolnaDetaljiInfo.proizvodjac?.PartnerNaziv || 'N/A'}</b>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section: Dokument (krajnje removed from here) */}
                                    <div className='card shadow-sm border-0 mt-4'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='fw-bold text-dark'>2) Dokument</div>
                                            <div className='text-muted small'>Broj naloga</div>

                                            <div className='row g-3 align-items-end mt-1'>
                                                <div className='col-12 col-md-4 col-xl-3'>
                                                    <label className='form-label fw-semibold'>Skeniraj nalog</label>
                                                    <div className='d-flex align-items-center gap-2'>
                                                        <QrCodeScanner
                                                            onScan={(data) => {
                                                                const nalogText = extractNalogFromScan(String(data || ''))
                                                                formik.setFieldValue('BrojDokumentaIzlaza', nalogText)
                                                            }}
                                                        />
                                                        <span className='text-muted small'>QR</span>
                                                    </div>
                                                </div>

                                                <div className='col-12 col-md-8 col-xl-9'>
                                                    <label className='form-label fw-semibold required'>Broj naloga</label>
                                                    <input
                                                        type='text'
                                                        placeholder='Unesite broj naloga'
                                                        {...formik.getFieldProps('BrojDokumentaIzlaza')}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid':
                                                                formik.touched.BrojDokumentaIzlaza && formik.errors.BrojDokumentaIzlaza,
                                                            'is-valid':
                                                                formik.touched.BrojDokumentaIzlaza && !formik.errors.BrojDokumentaIzlaza,
                                                        })}
                                                    />
                                                    <FieldError name='BrojDokumentaIzlaza' />
                                                </div>

                                                {idFromURL && (
                                                    <div className='col-12 col-md-6'>
                                                        <label className='form-label fw-semibold'>Promet</label>
                                                        <input
                                                            type='number'
                                                            placeholder='PROMET'
                                                            {...formik.getFieldProps('Promet')}
                                                            className={clsx('form-control form-control-lg form-control-solid', {
                                                                'is-invalid': formik.touched.Promet && formik.errors.Promet,
                                                                'is-valid': formik.touched.Promet && !formik.errors.Promet,
                                                            })}
                                                        />
                                                        <FieldError name='Promet' />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section: Lokacije + Meta */}
                                    <div className='card shadow-sm border-0 mt-4'>
                                        <div className='card-body p-3 p-md-4'>
                                            <div className='fw-bold text-dark'>3) Lokacije & meta</div>
                                            <div className='text-muted small'>Odjel, poslovnica, lokacija, datum, vrsta izlaza</div>

                                            <div className='row g-3 mt-1'>
                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold required'>Odjel</label>
                                                    <OdjeliDropdown
                                                        value={formik.values.Odjel}
                                                        onChange={(name, value) => formik.setFieldValue('Odjel', value)}
                                                        errors={formik.errors.Odjel}
                                                        touched={formik.touched.Odjel}
                                                        selectName='Odjel'
                                                    />
                                                    <FieldError name='Odjel' />
                                                </div>

                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold required'>Poslovnica</label>
                                                    <PoslovniceDropdown
                                                        value={formik.values.Poslovnica}
                                                        onChange={(name, value) => formik.setFieldValue('Poslovnica', value)}
                                                        errors={formik.errors.Poslovnica}
                                                        touched={formik.touched.Poslovnica}
                                                        selectName='Poslovnica'
                                                    />
                                                    <FieldError name='Poslovnica' />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label fw-semibold required'>Lokacija</label>
                                                    <LokacijeRepromaterijalaDropdown
                                                        value={formik.values.LokacijaRolne}
                                                        onChange={(name, value) => formik.setFieldValue('LokacijaRolne', value)}
                                                        errors={formik.errors.LokacijaRolne}
                                                        touched={formik.touched.LokacijaRolne}
                                                        selectName='LokacijaRolne'
                                                    />
                                                    <FieldError name='LokacijaRolne' />
                                                </div>

                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold required'>Datum</label>
                                                    <input
                                                        type='date'
                                                        {...formik.getFieldProps('Datum')}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': formik.touched.Datum && formik.errors.Datum,
                                                            'is-valid': formik.touched.Datum && !formik.errors.Datum,
                                                        })}
                                                    />
                                                    <FieldError name='Datum' />
                                                </div>

                                                <div className='col-12 col-md-6'>
                                                    <label className='form-label fw-semibold required'>Vrsta izlaza</label>
                                                    <select
                                                        {...formik.getFieldProps('VrstaIzlaza')}
                                                        className={clsx('form-select form-select-lg form-select-solid', {
                                                            'is-invalid': formik.touched.VrstaIzlaza && formik.errors.VrstaIzlaza,
                                                            'is-valid': formik.touched.VrstaIzlaza && !formik.errors.VrstaIzlaza,
                                                        })}
                                                    >
                                                        <option value=''>Vrsta Izlaza...</option>
                                                        <option value='1'>Proizvodnja</option>
                                                        <option value='2'>Direktna prodaja</option>
                                                    </select>
                                                    <FieldError name='VrstaIzlaza' />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label fw-semibold'>Napomena</label>
                                                    <input
                                                        type='text'
                                                        placeholder='Napomena'
                                                        {...formik.getFieldProps('Napomene')}
                                                        className={clsx('form-control form-control-lg form-control-solid', {
                                                            'is-invalid': formik.touched.Napomene && formik.errors.Napomene,
                                                            'is-valid': formik.touched.Napomene && !formik.errors.Napomene,
                                                        })}
                                                    />
                                                    <FieldError name='Napomene' />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT: DETAILS + ACTIONS */}
                                <div className='col-12 col-lg-5'>
                                    <div className='d-flex flex-column gap-4'>
                                        <div className='card shadow-sm border-0 d-none d-lg-block'>
                                            <div className='card-body p-3 p-md-4'>
                                                <div className='d-flex align-items-start justify-content-between gap-2'>
                                                    <div>
                                                        <div className='fw-bold text-dark'>Detalji rolne</div>
                                                        <div className='text-muted small'>Učitava se automatski nakon unosa</div>
                                                    </div>
                                                    <span className={clsx('badge', isScanned ? 'badge-light-success' : 'badge-light-danger')}>
                            {isScanned ? 'Skenirano' : 'Nije skenirano'}
                          </span>
                                                </div>

                                                <div className='mt-3'>
                                                    {!rolnaDetaljiInfo ? (
                                                        <div className='alert alert-light border mb-0'>
                                                            <span className='text-muted'>Unesite rolna da se učitaju detalji.</span>
                                                        </div>
                                                    ) : rolnaDetaljiInfo?.message ? (
                                                        <div className='alert alert-warning mb-0'>
                                                            <b>Poruka:</b> {rolnaDetaljiInfo.message}
                                                        </div>
                                                    ) : (
                                                        <div className='alert alert-primary mb-0'>
                                                            <div className='d-flex flex-column gap-2'>
                                                                <div>
                                                                    Težina / Količina: <b>{rolnaDetaljiInfo.ReprMatTrenutnaKolicina || 'N/A'}</b>
                                                                </div>
                                                                <div>
                                                                    Debljina: <b>{rolnaDetaljiInfo.ReprMatDebljina || 'N/A'}</b>
                                                                </div>
                                                                <div>
                                                                    Širina: <b>{rolnaDetaljiInfo.ReprMatSirina || 'N/A'}</b>
                                                                </div>
                                                                <div>
                                                                    Boja: <b>{rolnaDetaljiInfo.boja_lica?.BojaNaziv || 'N/A'}</b>
                                                                </div>
                                                                <div>
                                                                    Proizvođač: <b>{rolnaDetaljiInfo.proizvodjac?.PartnerNaziv || 'N/A'}</b>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {scannedByDevice === 1 && rawScan && (
                                                    <div className='text-muted small mt-3'>
                                                        <i className='bi bi-upc-scan me-1' />
                                                        Payload: <span className='fw-semibold'>{rawScan}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Summary + CTA (NaN-safe) */}
                                        <div className='card shadow-sm border-0'>
                                            <div className='card-body p-3 p-md-4'>
                                                <div className='fw-bold text-dark'>Brza provjera prije potvrde</div>

                                                <div className='mt-3'>
                                                    <div className='d-flex justify-content-between py-2 border-bottom'>
                                                        <span className='text-muted'>Rolna</span>
                                                        <span className='fw-semibold text-end ms-2'>
                                                          {formik.values.Repromaterijal ? String(formik.values.Repromaterijal) : '—'}
                                                        </span>
                                                    </div>

                                                    <div className='d-flex justify-content-between py-2 border-bottom'>
                                                        <span className='text-muted'>Krajnja količina</span>
                                                        <span className='fw-semibold text-end ms-2'>
                                                          {formik.values.Krajnje === '' || formik.values.Krajnje === null || Number.isNaN(formik.values.Krajnje as any)
                                                              ? '—'
                                                              : String(formik.values.Krajnje)}
                                                        </span>
                                                    </div>

                                                    <div className='d-flex justify-content-between py-2 border-bottom'>
                                                        <span className='text-muted'>Broj naloga</span>
                                                        <span className='fw-semibold text-end ms-2'>
                                                          {formik.values.BrojDokumentaIzlaza ? String(formik.values.BrojDokumentaIzlaza) : '—'}
                                                        </span>
                                                    </div>

                                                    <div className='d-flex justify-content-between py-2 border-bottom'>
                                                        <span className='text-muted'>Datum</span>
                                                        <span className='fw-semibold text-end ms-2'>{formik.values.Datum || '—'}</span>
                                                    </div>

                                                    <div className='d-flex justify-content-between py-2'>
                                                        <span className='text-muted'>Vrsta izlaza</span>
                                                        <span className='fw-semibold text-end ms-2'>
                                                          {String(formik.values.VrstaIzlaza) === '1'
                                                              ? 'Proizvodnja'
                                                              : String(formik.values.VrstaIzlaza) === '2'
                                                                  ? 'Direktna prodaja'
                                                                  : '—'}
                                                        </span>
                                                    </div>
                                                </div>

                                                {rolnaDetaljiInfo?.message ? (
                                                    <div className='alert alert-warning mt-4 mb-0'>
                                                        <div className='fw-semibold'>
                                                            <i className='bi bi-exclamation-triangle me-2' />
                                                            Ne možete potvrditi
                                                        </div>
                                                        <div className='text-muted small'>Ispravite rolna podatke:</div>
                                                        <div className='mt-1'>{rolnaDetaljiInfo.message}</div>
                                                    </div>
                                                ) : (
                                                    <div className='alert alert-light border mt-4 mb-0'>
                                                        <i className='bi bi-shield-check me-2' />
                                                        <span className='text-muted'>Sve izgleda OK. Možete potvrditi.</span>
                                                    </div>
                                                )}

                                                <div className='d-grid mt-4'>
                                                    <button type='submit' className='btn btn-primary btn-lg' disabled={disableSubmit}>
                                                        {!loading ? (
                                                            <>
                                                                <i className='bi bi-check2-circle me-2' />
                                                                Potvrdi
                                                            </>
                                                        ) : (
                                                            <span className='d-inline-flex align-items-center gap-2'>
                                Please wait...
                                <span className='spinner-border spinner-border-sm align-middle' />
                              </span>
                                                        )}
                                                    </button>
                                                </div>

                                                {!formik.isValid && (
                                                    <div className='text-danger small mt-3'>
                                                        <i className='bi bi-x-circle me-1' />
                                                        Neka obavezna polja nisu ispravna.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile bottom bar */}
                        <div className='d-lg-none border-top p-3 p-md-4 bg-white'>
                            <button type='submit' className='btn btn-primary btn-lg w-100' disabled={disableSubmit}>
                                {!loading ? (
                                    <>
                                        <i className='bi bi-check2-circle me-2' />
                                        Potvrdi
                                    </>
                                ) : (
                                    <span className='d-inline-flex align-items-center gap-2'>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle' />
                  </span>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

export default PrometDetails
